import FetchAccountMetrics from '@Application/AccountMetrics/Queries/FetchAccountMetrics';
import fetchAccountMetrics from '@Application/AccountMetrics/fetchAccountMetrics';
import { PermissionEnum } from '@Application/Security/Roles';
import PageHeader from '@Guidelines/Layout/PageHeader';
import AccountLogoCard from '@UI/Pages/Administration/Organizations/AccountLogoCard';
import LogoSidebar from '@UI/Pages/Administration/Organizations/Sidebars/LogoSidebar';
import ConsolidatedMetrics from '@UI/Pages/Administration/Organizations/ConsolidatedMetrics';
import OrganizationsContent from '@UI/Pages/Administration/Organizations/OrganizationsContent';
import OrganizationsConfirmationDialogs from '@UI/Pages/Administration/Organizations/ConfirmationDialogs/OrganizationsConfirmationDialogs';
import OrganizationsNotifications from '@UI/Pages/Administration/Organizations/OrganizationsNotifications';
import OrganizationSidebar from '@UI/Pages/Administration/Organizations/Sidebars/OrganizationSidebar';
import { openOrganizationSidebar } from '@UI/Pages/Administration/Organizations/Signals/organizationSidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Divider } from '@verclam/ui-kit';
import PageContainer from '@Guidelines/Layout/PageContainer';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { Grid, GridItem } from '@verclam/grid-system';

/**
 * Assembles the page: header, the consolidated metrics block, the data-owning `OrganizationsContent`,
 * `OrganizationSidebar`, `OrganizationsConfirmationDialogs` and notifications — unconditional siblings of the
 * list rather than repeated across its loading/error/empty/nominal branches. The consolidated block
 * stays here rather than inside `OrganizationsContent`: it is gated on its own permission and renders
 * independently of the list's own state, so folding it into the branch logic would mean repeating it
 * in every branch instead of mounting it once.
 */
const OrganizationsPage: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const canReadConsolidated = useHasPermission(PermissionEnum.ORGANIZATION_CONSOLIDATED_READ);

	// The `/metrics` call is gated on `ORGANIZATION_CONSOLIDATED_READ`: firing it for every
	// `ORGANIZATION_WRITE` holder would draw a `403` (and its error toast) on every page open.
	const { data: accountMetrics } = useStoreQuery({
		selector: (state) => state.accountMetricsStore.accountMetrics,
		stopOn: (value) => !canReadConsolidated || value !== null,
		query: () => {
			if (!canReadConsolidated || accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountMetrics(new FetchAccountMetrics(accountId)));
		},
		deps: [accountId, canReadConsolidated],
	});

	const handleCreateClick = React.useCallback(() => {
		openOrganizationSidebar(null);
	}, []);

	return (
		<PageContainer>
			<PageHeader
				eyebrow="STRUCTURE"
				title="Structures"
				actions={
					<WithPermission permission={PermissionEnum.ORGANIZATION_WRITE}>
						<Button
							type="button"
							label="Nouvelle structure"
							icon={<Plus size={16} />}
							onClick={handleCreateClick}
						/>
					</WithPermission>
				}
			/>
			<div
				css={{
					display: 'flex',
					flexDirection: 'column',
					gap: '1.5rem',
					overflowY: 'auto',
					padding: '0 1rem',
				}}
			>
				<Grid columns={2} columnGap={1} rowGap={1}>
					<WithPermission permission={PermissionEnum.ORGANIZATION_CONSOLIDATED_READ}>
						<ConditionalLoader data={accountMetrics}>
							{(props) => (
								<GridItem columnSize={{ xl: 1, lg: 1, md: 2 }} css={{ height: '100%' }}>
									<ConsolidatedMetrics metrics={props} />
								</GridItem>
							)}
						</ConditionalLoader>
					</WithPermission>
					<GridItem columnSize={{ xl: 1, lg: 1, md: 2 }} css={{ height: '100%' }}>
						<WithPermission permission={PermissionEnum.ACCOUNT_LOGO_WRITE}>
							<AccountLogoCard />
						</WithPermission>
					</GridItem>
				</Grid>

				<Divider />

				<OrganizationsContent />
			</div>
			<OrganizationSidebar />
			<LogoSidebar />
			<OrganizationsConfirmationDialogs />
			<OrganizationsNotifications />
		</PageContainer>
	);
};

export default OrganizationsPage;
